import Box from '@mui/material/Box';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { useCallback, useMemo } from 'react';

import useValidate from '@maya/hooks/useValidate';

import type { TextFieldVariants } from '@mui/material/TextField';
import type { DatePickerProps } from '@mui/x-date-pickers/DatePicker';
import type { Dayjs } from 'dayjs';
import type { FC, ReactNode } from 'react';

export interface DateFieldProps extends Omit<DatePickerProps<Dayjs>, 'onChange' | 'defaultValue' | 'value'> {
  defaultValue: string | undefined;
  required?: boolean;
  helperText?: ReactNode;
  variant?: TextFieldVariants;
  fullWidth?: boolean;
  pattern?: string;
  error?: boolean;
  'data-testid'?: string;
  onChange: (value: string | undefined) => void;
  onValidate?: (valid: boolean) => void;
}

const DateField: FC<DateFieldProps> = ({
  required = false,
  defaultValue,
  helperText,
  variant = 'filled',
  fullWidth = true,
  pattern,
  error,
  onChange,
  onValidate,
  'data-testid': dataTestId,
  ...props
}) => {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const defaultDateValue = useMemo(() => (defaultValue ? dayjs(defaultValue) : undefined), []);

  const { validate, hint, hasError } = useValidate({
    defaultValue: defaultDateValue,
    required,
    pattern,
    error,
    helperText,
    onValidate
  });

  const handleOnChange = useCallback(
    (newValue: Dayjs | null) => {
      const formattedValue = newValue?.format('YYYY-MM-DD');
      onChange(formattedValue);
      validate(newValue);
    },
    [onChange, validate]
  );

  return (
    <Box data-testid={dataTestId} sx={{ width: fullWidth ? '100%' : undefined }}>
      <DatePicker
        defaultValue={defaultDateValue}
        onChange={handleOnChange}
        slotProps={{
          textField: {
            helperText: hint ?? <>&nbsp;</>,
            required,
            variant,
            fullWidth,
            error: hasError
          }
        }}
        {...props}
      />
    </Box>
  );
};

export default DateField;
